// src/services/KitchenStaffService.js - FIXED Kitchen Staff Management

import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import AuthService from './AuthService';

const KitchenStaffService = {
  /**
   * Create a kitchen staff member
   */
  async createKitchenStaff(restaurantId, staffData) {
    try {
      console.log('🔐 Creating kitchen staff for restaurant:', restaurantId);

      // Get restaurant to check package limits
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      const restaurantDoc = await getDoc(restaurantRef);
      
      if (!restaurantDoc.exists()) {
        return { success: false, message: 'Restaurant not found' };
      }

      const restaurant = restaurantDoc.data();

      // Check if restaurant is active
      if (restaurant.status !== 'Active') {
        return { success: false, message: 'Restaurant is inactive' };
      }

      // Get package to check device limit
      const packagesSnapshot = await getDocs(collection(db, 'packages'));
      const restaurantPackage = packagesSnapshot.docs
        .find(doc => doc.data().name === restaurant.package)
        ?.data();

      const maxDevices = restaurantPackage?.maxKitchenDevices || 2; // Default 2 devices

      // Check existing staff count
      const staffQuery = query(
        collection(db, 'restaurants', restaurantId, 'kitchen_staff'),
        where('active', '==', true)
      );
      const staffSnapshot = await getDocs(staffQuery);
      
      if (staffSnapshot.size >= maxDevices) {
        return { 
          success: false, 
          message: `Maximum ${maxDevices} kitchen staff accounts allowed for ${restaurant.package} package` 
        };
      }

      // Create Firebase Auth account
      const authResult = await AuthService.createUser(
        staffData.email,
        staffData.password,
        { role: 'kitchen_staff', restaurantId }
      );

      if (!authResult.success) {
        return { success: false, message: authResult.message };
      }

      // Create staff document
      const staffId = `staff_${Date.now()}`;
      const staffDoc = {
        id: staffId,
        firebaseUid: authResult.uid,
        name: staffData.name,
        email: staffData.email,
        restaurantId: restaurantId, // ✅ STORE RESTAURANT ID
        role: 'kitchen_staff',
        active: true,
        deviceId: null,
        lastLogin: null,
        createdAt: new Date().toISOString(),
        createdBy: staffData.createdBy || 'Owner'
      };

      const staffRef = doc(db, 'restaurants', restaurantId, 'kitchen_staff', staffId);
      await setDoc(staffRef, staffDoc);

      // ✅ ALSO CREATE A GLOBAL LOOKUP DOCUMENT (for easy email->restaurant mapping)
      const lookupRef = doc(db, 'kitchen_staff_lookup', authResult.uid);
      await setDoc(lookupRef, {
        email: staffData.email,
        restaurantId: restaurantId,
        staffId: staffId,
        createdAt: new Date().toISOString()
      });

      console.log('✅ Kitchen staff created:', staffId);

      return {
        success: true,
        staffId: staffId,
        email: staffData.email,
        message: 'Kitchen staff created successfully'
      };

    } catch (error) {
      console.error('❌ Error creating kitchen staff:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Get all kitchen staff for a restaurant
   */
  async getKitchenStaff(restaurantId) {
    try {
      const staffSnapshot = await getDocs(
        collection(db, 'restaurants', restaurantId, 'kitchen_staff')
      );

      const staff = staffSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return staff;
    } catch (error) {
      console.error('❌ Error getting kitchen staff:', error);
      return [];
    }
  },

  /**
   * ✅ FIXED: Verify kitchen staff login using Firebase Auth UID
   */
 /**
 * Verify kitchen staff login and device
 */
async verifyKitchenLogin(email, deviceId) {
  try {
    console.log('🔍 Verifying kitchen staff login:', email);

    // Get all restaurants
    const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
    
    let staffDoc = null;
    let restaurantId = null;
    let restaurantData = null;

    // Search for kitchen staff across all restaurants
    for (const restaurantDoc of restaurantsSnapshot.docs) {
      const restaurant = restaurantDoc.data();
      
      // Check if restaurant is active
      if (restaurant.status !== 'Active') {
        continue;
      }

      const staffQuery = query(
        collection(db, 'restaurants', restaurantDoc.id, 'kitchen_staff'),
        where('email', '==', email),
        where('active', '==', true)
      );
      
      const staffSnapshot = await getDocs(staffQuery);

      if (!staffSnapshot.empty) {
        staffDoc = staffSnapshot.docs[0];
        restaurantId = restaurantDoc.id;
        restaurantData = restaurant;
        break;
      }
    }

    if (!staffDoc) {
      return { success: false, message: 'Kitchen staff account not found or inactive' };
    }

    const staff = staffDoc.data();

    // Check device
    if (staff.deviceId && staff.deviceId !== deviceId) {
      return { 
        success: false, 
        message: 'This account is already logged in on another device. Please logout from the other device first.' 
      };
    }

    // Update device ID and last login
    await updateDoc(staffDoc.ref, {
      deviceId: deviceId,
      lastLogin: new Date().toISOString()
    });

    console.log('✅ Kitchen staff login verified');

    return {
      success: true,
      staffId: staff.id,
      restaurantId: restaurantId,
      name: staff.name,
      restaurantName: restaurantData.name
    };

  } catch (error) {
    console.error('❌ Error verifying kitchen login:', error);
    return { success: false, message: error.message };
  }
},

  /**
   * Logout kitchen staff (clear device ID)
   */
  async logoutKitchenStaff(restaurantId, staffId) {
    try {
      const staffRef = doc(db, 'restaurants', restaurantId, 'kitchen_staff', staffId);
      
      await updateDoc(staffRef, {
        deviceId: null,
        lastLogout: new Date().toISOString()
      });

      console.log('✅ Kitchen staff logged out:', staffId);
      return { success: true };

    } catch (error) {
      console.error('❌ Error logging out kitchen staff:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Update kitchen staff status
   */
  async updateKitchenStaffStatus(restaurantId, staffId, active) {
    try {
      const staffRef = doc(db, 'restaurants', restaurantId, 'kitchen_staff', staffId);
      
      await updateDoc(staffRef, {
        active: active,
        deviceId: active ? null : null, // Clear device if deactivating
        updatedAt: new Date().toISOString()
      });

      console.log(`✅ Kitchen staff ${active ? 'activated' : 'deactivated'}:`, staffId);
      return { success: true };

    } catch (error) {
      console.error('❌ Error updating kitchen staff:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Delete kitchen staff
   */
  async deleteKitchenStaff(restaurantId, staffId) {
    try {
      // Get staff doc to get firebaseUid
      const staffRef = doc(db, 'restaurants', restaurantId, 'kitchen_staff', staffId);
      const staffDoc = await getDoc(staffRef);

      if (staffDoc.exists()) {
        const staff = staffDoc.data();
        
        // Delete staff document
        await deleteDoc(staffRef);

        // Delete lookup document
        if (staff.firebaseUid) {
          const lookupRef = doc(db, 'kitchen_staff_lookup', staff.firebaseUid);
          await deleteDoc(lookupRef);
        }
      }

      console.log('✅ Kitchen staff deleted:', staffId);
      return { success: true };

    } catch (error) {
      console.error('❌ Error deleting kitchen staff:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Generate unique device ID
   */
 generateDeviceId() {
  // ✅ Check if device ID already exists in localStorage
  const existingDeviceId = localStorage.getItem('kitchenDeviceId');
  if (existingDeviceId) {
    console.log('📱 Using existing device ID:', existingDeviceId);
    return existingDeviceId;
  }

  // Generate new device ID with MORE unique identifiers
  const navigator_info = window.navigator;
  const screen_info = window.screen;
  
  // Collect comprehensive device fingerprint
  let fingerprint = [  // ✅ CHANGE const to let
    navigator_info.userAgent || 'unknown',
    navigator_info.language || 'unknown',
    navigator_info.platform || 'unknown',
    navigator_info.hardwareConcurrency || 0,
    screen_info.height,
    screen_info.width,
    screen_info.colorDepth,
    screen_info.pixelDepth,
    navigator_info.maxTouchPoints || 0,
    navigator_info.vendor || 'unknown',
    new Date().getTimezoneOffset(),
    navigator_info.mimeTypes.length,
    navigator_info.plugins.length
  ].join('|');
  
  // Add WebGL fingerprint
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  if (gl) {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      fingerprint += '|' + gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      fingerprint += '|' + gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    }
  }
  
  // Create hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  // ✅ Add random component to ensure uniqueness
  const randomPart = Math.random().toString(36).substring(2, 8);
  const newDeviceId = `device_${Math.abs(hash)}_${randomPart}`;
  
  localStorage.setItem('kitchenDeviceId', newDeviceId);
  console.log('📱 Generated new device ID:', newDeviceId);
  
  return newDeviceId;
}
};

export default KitchenStaffService;